/**
 * Bug #19 Fix: LocalStorage Management with TTL and Cleanup
 *
 * This composable provides safe localStorage operations with:
 * - Time-to-live (TTL) based expiration
 * - Automatic cleanup of old data
 * - Quota management
 * - Error handling for quota exceeded
 *
 * Prevents infinite localStorage growth
 */

const STORAGE_PREFIX = 'chatwoot_kanban_';
const MAX_ITEMS = 1000; // Maximum items to store
const DEFAULT_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

export function useKanbanStorage() {
  /**
   * Clean up old/expired data
   * @param {number} accountId - Account ID for namespacing
   */
  const cleanupOldData = accountId => {
    try {
      const prefix = `${STORAGE_PREFIX}${accountId}_`;
      const keysToRemove = [];
      const items = [];

      // Collect all items for this account
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          try {
            const item = JSON.parse(localStorage.getItem(key));
            items.push({
              key,
              createdAt: item.createdAt || 0,
              expiresAt: item.expiresAt || 0,
            });
          } catch (error) {
            // Invalid JSON - mark for removal
            keysToRemove.push(key);
          }
        }
      }

      // Remove expired items
      const now = Date.now();
      items.forEach(item => {
        if (item.expiresAt && now > item.expiresAt) {
          keysToRemove.push(item.key);
        }
      });

      // If still too many items, remove oldest
      if (items.length > MAX_ITEMS) {
        const sorted = items.sort((a, b) => a.createdAt - b.createdAt);
        const toRemove = sorted.slice(0, items.length - MAX_ITEMS);
        toRemove.forEach(item => keysToRemove.push(item.key));
      }

      // Remove marked keys
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // eslint-disable-next-line no-console
      console.log(`[KanbanStorage] Cleaned up ${keysToRemove.length} items`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[KanbanStorage] Error during cleanup:', error);
    }
  };

  /**
   * Get item from localStorage with TTL check
   * @param {string} key - Storage key
   * @param {number} accountId - Account ID for namespacing
   * @returns {any|null} - Parsed value or null if expired/not found
   */
  const getItem = (key, accountId) => {
    try {
      const fullKey = `${STORAGE_PREFIX}${accountId}_${key}`;
      const item = localStorage.getItem(fullKey);

      if (!item) return null;

      const parsed = JSON.parse(item);

      // Check if item has expired
      if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
        // Remove expired item
        localStorage.removeItem(fullKey);
        return null;
      }

      return parsed.value;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[KanbanStorage] Error reading from localStorage:', error);
      return null;
    }
  };

  /**
   * Set item in localStorage with TTL
   * @param {string} key - Storage key
   * @param {any} value - Value to store
   * @param {number} accountId - Account ID for namespacing
   * @param {number} ttl - Time to live in milliseconds
   */
  const setItem = (key, value, accountId, ttl = DEFAULT_TTL) => {
    try {
      const fullKey = `${STORAGE_PREFIX}${accountId}_${key}`;
      const item = {
        value,
        createdAt: Date.now(),
        expiresAt: Date.now() + ttl,
      };

      // Try to set item
      localStorage.setItem(fullKey, JSON.stringify(item));
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        // eslint-disable-next-line no-console
        console.warn(
          '[KanbanStorage] localStorage quota exceeded - running cleanup'
        );
        // Run cleanup and retry
        cleanupOldData(accountId);

        try {
          const fullKey = `${STORAGE_PREFIX}${accountId}_${key}`;
          const item = {
            value,
            createdAt: Date.now(),
            expiresAt: Date.now() + ttl,
          };
          localStorage.setItem(fullKey, JSON.stringify(item));
        } catch (retryError) {
          // eslint-disable-next-line no-console
          console.error(
            '[KanbanStorage] Failed to store even after cleanup:',
            retryError
          );
        }
      } else {
        // eslint-disable-next-line no-console
        console.error('[KanbanStorage] Error writing to localStorage:', error);
      }
    }
  };

  /**
   * Remove item from localStorage
   * @param {string} key - Storage key
   * @param {number} accountId - Account ID for namespacing
   */
  const removeItem = (key, accountId) => {
    try {
      const fullKey = `${STORAGE_PREFIX}${accountId}_${key}`;
      localStorage.removeItem(fullKey);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[KanbanStorage] Error removing from localStorage:', error);
    }
  };

  /**
   * Get all keys for a specific account
   * @param {number} accountId - Account ID
   * @returns {string[]} - Array of keys
   */
  const getAllKeys = accountId => {
    try {
      const prefix = `${STORAGE_PREFIX}${accountId}_`;
      const keys = [];

      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keys.push(key.substring(prefix.length));
        }
      }

      return keys;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[KanbanStorage] Error getting keys:', error);
      return [];
    }
  };

  /**
   * Clear all data for account
   * @param {number} accountId - Account ID
   */
  const clearAccountData = accountId => {
    try {
      const prefix = `${STORAGE_PREFIX}${accountId}_`;
      const keysToRemove = [];

      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));

      // eslint-disable-next-line no-console
      console.log(
        `[KanbanStorage] Cleared ${keysToRemove.length} items for account ${accountId}`
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[KanbanStorage] Error clearing account data:', error);
    }
  };

  return {
    getItem,
    setItem,
    removeItem,
    cleanupOldData,
    getAllKeys,
    clearAccountData,
  };
}
